import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BottomTab from "../../components/common/bottom-tab";
import { gql, useQuery } from "@apollo/client";
import { GET_ORDERS } from "../order/page";
import Header from "../../components/common/header";
import TransitionWrapper from "../../components/common/transition-wrapper";
import { notify } from "../../utils/toast";
import { ME_QUERY } from "../../api/user-queries";
import FrequencyCard from "../../components/frequency/frequency-card";
import { HiCog6Tooth, HiGift, HiTicket, HiBanknotes } from "react-icons/hi2";
import Lottie from "lottie-react";
import { track } from "@amplitude/analytics-browser";
import { useAuth } from "../../core/auth";

const GET_TOTAL_DISCOUNT = gql`
  query getTotalDiscount {
    getTotalDiscount
  }
`;

const My = () => {
  const { data } = useQuery(GET_ORDERS);
  const { data: totalDiscountData } = useQuery(GET_TOTAL_DISCOUNT);
  const { data: meData } = useQuery(ME_QUERY, { fetchPolicy: "network-only" });

  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [piggyData, setPiggyData] = useState<object | null>(null);
  const [earthData, setEarthData] = useState<object | null>(null);

  const piggyRef = useRef<HTMLDivElement>(null);
  const earthRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { loginAdmin, changeLastPage } = useAuth();

  async function handleSellerLoginClick() {
    const sellerToken = localStorage.getItem("sellerToken");
    if (sellerToken) {
      await loginAdmin();
      changeLastPage();
      navigate("/boss/home");
    } else {
      navigate("/boss/login");
    }
  }

  // Lazy load piggy animation
  useEffect(() => {
    if (!piggyData && piggyRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            import("../../assets/lottie/piggy.json").then((module) => {
              setPiggyData(module.default);
            });
            observer.disconnect();
          }
        },
        { rootMargin: "200px", threshold: 0.1 }
      );
      observer.observe(piggyRef.current);
      return () => observer.disconnect();
    }
  }, [piggyData]);

  // Lazy load earth animation
  useEffect(() => {
    if (!earthData && earthRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            import("../../assets/lottie/earth.json").then((module) => {
              setEarthData(module.default);
            });
            observer.disconnect();
          }
        },
        { rootMargin: "200px", threshold: 0.1 }
      );
      observer.observe(earthRef.current);
      return () => observer.disconnect();
    }
  }, [earthData]);

  useEffect(() => {
    if (totalDiscountData?.getTotalDiscount) {
      setTotalDiscount(totalDiscountData?.getTotalDiscount);
    }

    if (data?.orders) {
      const orders = data.orders;
      const getTotalAmount = () => {
        return orders.reduce((total, item) => {
          return total + item?.amount;
        }, 0);
      };
      const total = getTotalAmount();
      setTotalAmount(total);
    }
  }, [data?.orders, totalDiscountData?.getTotalDiscount]);

  function calculateCarbonEmission(amount: number) {
    // 입력된 금액을 1,000원 단위로 올림
    const roundedAmount = Math.ceil(amount / 1000) * 1000;

    // 1,000원당 0.25kg을 곱해서 탄소 배출량 계산
    const carbonEmission = (roundedAmount / 1000) * 0.25;

    return carbonEmission;
  }

  const copyReferralCode = () => {
    if (meData?.me?.referralCode) {
      navigator.clipboard.writeText(meData.me.referralCode);
      notify("추천인 코드가 복사되었습니다.");
    }
  };
  return (
    <div className="w-full h-screen flex flex-col overflow-y-scroll">
      <Header title="마이페이지">
        <Header.RightContent>
          <div
            onClick={() => navigate("/my/settings")}
            className="flex items-center gap-0.5 bg-gray-100 rounded-full border px-2 py-1"
          >
            <HiCog6Tooth className="text-gray-600" size={24} />
            <span className="text-gray-700 font-medium text-md">설정</span>
          </div>
        </Header.RightContent>
      </Header>

      <div className="w-full space-y-6 flex flex-col mb-32">
        <div
          className="w-full px-4 pt-4 bg-white"
          onClick={() => navigate("/order")}
        >
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-2xl font-semibold text-gray-800">
              내 이용 내역
            </h3>
            <span className="text-md text-gray-500 hover:text-gray-700">
              자세히 보기
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <TransitionWrapper className="flex flex-col py-3 items-center bg-gradient-to-tr bg-[#ffede6] rounded-xl overflow-hidden">
              <div
                ref={piggyRef}
                className="h-36 overflow-hidden flex justify-center items-center"
              >
                {piggyData && (
                  <Lottie
                    animationData={piggyData}
                    className="h-40 mb-2"
                    loop={true}
                    autoplay={true}
                  />
                )}
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {totalDiscount.toLocaleString()}원
              </span>
              <span className="mt-1 text-md text-gray-600">절약했어요!</span>
            </TransitionWrapper>

            <TransitionWrapper className="flex flex-col py-3 items-center bg-gradient-to-tr bg-[#dbf7ff] rounded-xl overflow-hidden">
              <div
                ref={earthRef}
                className="h-36 overflow-hidden flex justify-center items-center"
              >
                {earthData && (
                  <Lottie
                    animationData={earthData}
                    className="h-40 mb-2"
                    loop={true}
                    autoplay={true}
                  />
                )}
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {calculateCarbonEmission(totalAmount)}kg
              </span>
              <span className="mt-1 text-md text-gray-600">CO₂ 줄였어요!</span>
            </TransitionWrapper>
          </div>
        </div>

        <div>
          <FrequencyCard />
        </div>

        <div className="w-full px-4 bg-white">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-semibold text-gray-800">쿠폰</h2>
            <div className="flex items-center gap-2">
              <span className="text-gray-700">추천인 코드: </span>
              <span className="text-primary font-black">
                {meData?.me?.referralCode}
              </span>
              <button
                className="bg-primary font-medium text-white text-md px-3 py-1.5 rounded-full active:bg-blue-600"
                onClick={() => copyReferralCode()}
              >
                복사
              </button>
            </div>
          </div>

          <div
            className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg cursor-pointer active:bg-gray-50"
            onClick={() => {
              track("레퍼럴 진입로 클릭", {
                어디서: "마이페이지",
              });
              navigate("/my/invite/intro");
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                <HiGift className="text-primary w-6 h-6 animate-wiggle-once" />
              </div>
              <div>
                <div className="text-black text-lg font-semibold">
                  <span className="text-primary bg-primary/10 px-1 py-0.5 rounded-md text-md">
                    1,OOO원
                  </span>{" "}
                  +{" "}
                  <span className="text-primary bg-primary/10 px-1 py-0.5 rounded-md text-md">
                    1,OOO원
                  </span>{" "}
                  할인 쿠폰 받기
                </div>
                <div className="text-gray-500 text-md">친구 초대 이벤트!</div>
              </div>
            </div>
            <div className="text-gray-400 text-lg">→</div>
          </div>

          <div
            className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg cursor-pointer active:bg-gray-50 mt-3"
            onClick={() => {
              track("SNS 인증 이벤트 진입", {
                어디서: "마이페이지",
              });
              navigate("/event/sns-promotion");
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                <HiGift className="text-purple-500 w-6 h-6 animate-wiggle-once" />
              </div>
              <div>
                <div className="text-black text-lg font-semibold">
                  <span className="text-purple-600 bg-purple-600/10 px-1 py-0.5 rounded-md text-md">
                    3,OOO원
                  </span>{" "}
                  할인 쿠폰 받기
                </div>
                <div className="text-gray-500 text-md">
                  SNS 인증하고 쿠폰 받기!
                </div>
              </div>
            </div>
            <div className="text-gray-400 text-lg">→</div>
          </div>

          <div
            className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg cursor-pointer active:bg-gray-50 mt-3"
            onClick={() => navigate("/my/coupon")}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                <HiTicket className="text-gray-600 w-6 h-6" />
              </div>
              <div>
                <div className="text-black text-lg font-semibold">
                  내 쿠폰함
                </div>
                <div className="text-gray-500 text-md">
                  발급받은 쿠폰을 확인해요
                </div>
              </div>
            </div>
            <div className="text-gray-400 text-lg">→</div>
          </div>

          <div
            className="flex justify-between items-center p-4 bg-white border border-gray-200 rounded-lg cursor-pointer active:bg-gray-50 mt-3"
            onClick={() => {
              track("예치금 페이백 진입", {
                어디서: "마이페이지",
              });
              navigate("/my/wallet");
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                <HiBanknotes className="text-[#1692fc] w-6 h-6" />
              </div>
              <div>
                <div className="text-black text-lg font-semibold">
                  예치금 / 페이백
                </div>
                <div className="text-gray-500 text-md">
                  잔액 조회 및 출금 요청
                </div>
              </div>
            </div>
            <div className="text-gray-400 text-lg">→</div>
          </div>

          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => navigate("/my/settings")}
              className="flex items-center gap-0.5 bg-gray-100 rounded-full border px-4 py-2 whitespace-nowrap"
            >
              <HiCog6Tooth className="text-gray-600" size={20} />
              <span className="text-gray-700 font-medium break-keep">
                설정 더보기
              </span>
            </button>
            <button
              onClick={() => handleSellerLoginClick()}
              className="flex items-center gap-0.5 bg-gray-100 rounded-full border px-4 py-2 whitespace-nowrap"
            >
              <span className="text-gray-700 font-medium break-keep">
                사장님 로그인하기
              </span>
            </button>
          </div>
        </div>
      </div>

      <BottomTab />
    </div>
  );
};

export default My;
